import { Badge } from "@comps/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@comps/ui/popover";
import { EditRightAccess, ObjectRightType, ViewRightType, hasGlobalRightAccess } from "@dto/Users";
import { FieldType, TYPE_ITEMS_CHOICE_OPTIONS, TYPE_ITEMS_FIELD_KEY, TYPE_ITEMS_OBJECT_KEY, TYPE_ITEMS_RELATION_KEY, fieldChoiceByNames, getDestinationField } from "@dto/fields";
import { Direction, FieldColumn, INBOUND, Item, OUTBOUND, areItemsEqual } from "@dto/records";
import { getRecordKeys } from "@utils/data";
import { hasFilter } from "@utils/filters";
import { getFieldByPathKeys, getRelationDirection, hasOnlyOneValue } from "@utils/model";
import { TW_FormulaError, TW_LinkBadge, TW_LinkBadgeReadOnly, TW_LinkCell, TW_LinkCellReadOnly, TW_PopoverFitViewport, getBadgeStyle } from "@utils/twClasses";
import { containsPosition, getLine, isArray, isFormulaError, itemToString, nextPosition, posToString, prevPosition, samePosition } from "@utils/types";
import { useEffect, useMemo, useRef, useState } from "react";
import { useIsMobile } from "@comps/hooks/use-mobile";
import { useTranslation } from "react-i18next";
import { useDataStore, useModelStore, useUserStore } from "src/store";
import { CellProps, NEW_ROW_ID } from "./GridCell";
import { LinkEditionForm } from "./LinkEditionForm";
import { isLinkChainField, isReadOnlyLinkChainColumn, linkedRecordKey, linkSourceKey } from "./linkChain";

interface LinkCellProps extends CellProps {
    fieldColumn: FieldColumn;
}

// Extracts the (first) choice name from a raw linked value, which may be a plain
// string, an array of choice names (multi-choice), or a wrapped { value } object.
function getFirstChoiceName(rawValue: any): string | undefined {
    if (rawValue === null || rawValue === undefined) {
        return undefined;
    }
    let value = rawValue;
    if (typeof value === "object" && !isArray(value) && value.value !== undefined) {
        value = value.value;
    }
    if (isArray(value)) {
        return value.length > 0 ? String(value[0]) : undefined;
    }
    return String(value);
}

export function LinkCell({ pathKey, field, item, position, isEditing, fieldColumn, noDataFetch = false }: LinkCellProps) {

    const data = useDataStore(state => state.data);
    const updateData = useDataStore(state => state.updateDataOnly);
    const colNumber = useDataStore(state => state.colNumber);
    const fieldByKeys = useModelStore(state => state.fieldByKeys);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const isMobile = useIsMobile();
    const [searchValue, setSearchValue] = useState("");
    const focus = useDataStore(state => state.focus);
    const edit = useDataStore(state => state.edit);
    const [value, setValue] = useState<Item>(item);
    const { t } = useTranslation();
    const fetchData = useDataStore(state => state.fetchData);
    const relationByKeys = useModelStore(state => state.relationByKeys);
    const objectInfo = useModelStore(state => state.selectedObjectInfos);
    const wsColor = useModelStore(state => state.wsColor);
    const badgeStyle = getBadgeStyle(wsColor);
    const metaLines = useDataStore(state => state.metaLines);
    const userRole = useModelStore(state => state.userRole);
    const user = useUserStore(state => state.user);
    const selectedObjectInfos = useModelStore(state => state.selectedObjectInfos);
    const selectedView = useModelStore(state => state.selectedView);
    const hasEditAccessOnObject = hasGlobalRightAccess(user, userRole, ObjectRightType, EditRightAccess, selectedObjectInfos?.object?._key);
    const hasEditAccessOnView = hasGlobalRightAccess(user, userRole, ViewRightType, EditRightAccess, selectedView?._key);
    const hasEditAccess = hasEditAccessOnObject && hasEditAccessOnView;
    const fieldByPathKeys = getFieldByPathKeys(selectedObjectInfos, selectedObjectInfos?.fields ?? [], relationByKeys, fieldByKeys);
    const setRowSelection = useDataStore(state => state.setRowSelection);
    const fieldColumns = useDataStore(state => state.fieldColumns);
    const aggregation = selectedView?.aggregations?.get(pathKey ?? "") ?? field?.aggregation;

    // Format of the displayed value (the end of the chain for a link chain)
    const format = getDestinationField(field, fieldByKeys)?.format;
    const fetchLine = useDataStore(state => state.fetchLine);

    useEffect(() => {
        if (!areItemsEqual(item, value)) {
            setValue(item);
        }
    }, [item]);

    useEffect(() => {
        if (isEditing) {
            // When editing was started by typing a character on the cell, the
            // grid stores that character in editInitialKey. Seed the search with
            // it so the first letter isn't lost (the controlled input picks it
            // up once it mounts, even while the records are still loading).
            const initialKey = useDataStore.getState().editInitialKey;
            if (initialKey && initialKey.length === 1) {
                setSearchValue(initialKey);
            }
            if (searchInputRef.current) {
                searchInputRef.current.focus();
            }
        }
    }, [isEditing]);

    const objectKey = field.typeItems!.get(TYPE_ITEMS_OBJECT_KEY);
    const fieldKey = field.typeItems!.get(TYPE_ITEMS_FIELD_KEY);
    const relationKey = field.typeItems!.get(TYPE_ITEMS_RELATION_KEY);

    const linkedField = fieldByKeys?.get(fieldKey);
    const destinationField = getDestinationField(field, fieldByKeys);
    // A link chain (link displaying a link of its target object) edits the link
    // of the row record, which its items do not carry: it is read from the line.
    const isChain = isLinkChainField(field, fieldByKeys);
    const metaLine = position ? metaLines.get(position.rowId) : undefined;
    const rowRecordKey = isChain && metaLine && objectInfo ? getRecordKeys(metaLine, objectInfo.object._key, true)[0] : undefined;
    const chainSourceKey = isChain ? linkSourceKey(item, field, fieldByKeys, objectInfo?.object._key, rowRecordKey) : null;
    const parentKey = isChain ? (chainSourceKey ?? { objectKey: objectInfo?.object._key ?? "", recordKey: "" }) : item?.parentKey;
    const relation = relationByKeys?.get(relationKey);
    let direction: Direction = getRelationDirection(relation, objectInfo?.object._key);
    const fieldsOnSameRelation = objectInfo?.fields?.filter((field) => {
        if (field.type === FieldType.Link) {
            return field.typeItems?.get(TYPE_ITEMS_RELATION_KEY) === relationKey;
        }
        return false;
    });
    // This could be optimized by checking only the visible fields
    const hasSeveralFieldsOnSameRelation = fieldsOnSameRelation && fieldsOnSameRelation.length > 1;
    const pathsInDependencies = useMemo(() => {
        const result = new Set<string>();
        fieldColumns.forEach(fieldColumn => {
            if (fieldColumn.dependencies) {
                fieldColumn.dependencies.forEach(pathKey => result.add(pathKey));
            }
        });
        return Array.from(result);
    }, [fieldColumns]);

    // When the destination field is a choice / multi-choice, badges are colored
    // with the matching choice value's color instead of the workspace color.
    const isChoiceDest = destinationField?.type === FieldType.Choice || destinationField?.type === FieldType.MultiChoice;
    const choiceByNames = useMemo(() => {
        if (!isChoiceDest) {
            return null;
        }
        return fieldChoiceByNames(destinationField?.typeItems?.get(TYPE_ITEMS_CHOICE_OPTIONS));
    }, [isChoiceDest, destinationField]);

    // Returns the badge style for a single linked value: the choice value's color
    // when the destination is a choice / multi-choice, otherwise the workspace color.
    function getValueBadgeStyle(rawValue: any) {
        if (choiceByNames) {
            const choiceName = getFirstChoiceName(rawValue);
            const choiceColor = choiceName ? choiceByNames.get(choiceName)?.color : undefined;
            if (choiceColor) {
                return getBadgeStyle(choiceColor, 1);
            }
        }
        return badgeStyle;
    }


    // If the parent key is not set, we are in the root of the tree
    // This should not happen
    if (!parentKey || !position) {
        return null;
    }


    function updateValue(newLinkedValue: Item) {
        if (!selectedObjectInfos?.object) {
            return;
        }
        const metaLine = metaLines.get(position!.rowId);
        var parentKey = item.parentKey;
        if (isChain) {
            // The parent of a chain item is the linked record (or the row record without link)
            parentKey = newLinkedValue.parentKey ?? item.parentKey;
        } else if (!parentKey?.recordKey && metaLine) {
            const lineRecordKeys = getRecordKeys(metaLine, objectInfo!.object._key);
            if (lineRecordKeys.length > 0) {
                parentKey = { recordKey: lineRecordKeys[0], objectKey: objectInfo!.object._key };
            }
        }
        const newItem = { ...item, value: newLinkedValue.value, key: newLinkedValue.key, parentKey: parentKey };
        // Record whose link was edited
        const sourceRecordKey = isChain ? chainSourceKey?.recordKey : parentKey?.recordKey;

        const line = getLine(data, position!.rowId);
        if (line) {
            line.items[position!.column] = newItem;
        }
        updateData(data);
        setValue(newItem);

        if (metaLine && metaLine.id === NEW_ROW_ID) {
            if (sourceRecordKey) {
                fetchLine(NEW_ROW_ID, [sourceRecordKey], selectedView).then(() => {
                    setTimeout(() => {
                        // The user may have moved on while the line was being read
                        if (containsPosition(useDataStore.getState().focusPos, position)) {
                            focus(position);
                        }
                    }, 50);
                });
            }
        } else {
            // Fetch data only if other lines can be impacted by this change
            if (relation && (hasSeveralFieldsOnSameRelation || (direction === OUTBOUND && relation.srcCard === 1) || (direction === INBOUND && relation.destCard === 1) || selectedView?.groups?.some(group => fieldByPathKeys.get(group.pathKey)?._key === field._key) ||
                selectedView?.sortings?.some(sorting => fieldByPathKeys.get(sorting.pathKey)?._key === field._key) ||
                hasFilter(selectedView?.filter, field._key, fieldByPathKeys) ||
                pathsInDependencies.includes(pathKey ?? ""))) {
                if (!noDataFetch) {
                    fetchData();
                    setRowSelection({});
                }
                setIsDialogOpen(false);
            }
        }

        // If only one value, we close the popover
        const onlyOneValue = hasOnlyOneValue(relation, objectInfo?.object._key);
        if (onlyOneValue) {
            setIsDialogOpen(false);
        }
    }

    function cancel() {
        setSearchValue("");
        focus(position);
    }

    const readOnly = field?.readOnly || value?.readOnly || !hasEditAccess ||
        (isChain && (!chainSourceKey || isReadOnlyLinkChainColumn(fieldColumn, relationByKeys, fieldByKeys)));

    function onEnterKeyPress(event: React.KeyboardEvent) {
        if (readOnly) {
            return;
        }
        switch (event.key) {
            case "Enter":
                event.preventDefault();
                if (!isEditing) {
                    edit(position);
                }
                break;
            case "Tab":
                let next = null;
                if (event.shiftKey) {
                    next = prevPosition(position, colNumber);
                } else {
                    next = nextPosition(position, colNumber);
                }
                focus(next);
                break;
            case "Escape":
                event.preventDefault();
                event.stopPropagation();
                cancel();
                break;
            default:
                // Fallback for when the trigger itself is focused (the grid
                // normally routes the first keystroke through GridCell, which
                // seeds via editInitialKey). Only printable characters start a
                // search; modifiers / navigation keys are ignored. The input is
                // controlled, so seed the state — assigning .value directly has
                // no effect and was the original cause of the lost first letter.
                if (!isEditing && event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
                    setSearchValue(event.key);
                    edit(position);
                }
        }
    }

    function onClick(event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) {
        const currentFocusPos = useDataStore.getState().focusPos;
        const isSelected = currentFocusPos && currentFocusPos.length === 1 && samePosition(currentFocusPos[0], position);
        if (!readOnly && isSelected && !isEditing) {
            event.stopPropagation();
            event.preventDefault();
            edit(position);
        }
    }

    function setIsDialogOpen(isOpen: boolean) {
        if (isOpen) {
            const currentFocusPos = useDataStore.getState().focusPos;
            const isSelected = currentFocusPos && currentFocusPos.length === 1 && samePosition(currentFocusPos[0], position);
            if (!readOnly && isSelected && !isEditing) {
                edit(position);
            }
        } else {
            // Moving the focus to another cell (Tab) closes the popover: it stays there
            const currentFocusPos = useDataStore.getState().focusPos;
            if (!currentFocusPos || containsPosition(currentFocusPos, position)) {
                focus(position);
            }
        }
    }



    const isArrayOfLinks = typeof value.value != "string" && isArray(value.value) && (Array.from(value.value).length === 0 || typeof value.value[0] !== "string");
    if (!isArrayOfLinks) {
        const valueStr = linkedField ? itemToString(value as Item, linkedField, aggregation, format, t, "", fieldByKeys) : value?.value || "";
        return (
            <Popover open={isEditing} onOpenChange={setIsDialogOpen}>
                <PopoverTrigger className={readOnly ? TW_LinkCellReadOnly : TW_LinkCell} onKeyDown={onEnterKeyPress} onClick={onClick} tabIndex={0}>
                    <div key={posToString(position) + "_div_" + field._key}>
                        {isFormulaError(value?.value, linkedField, fieldByKeys) ?
                            <span key={posToString(position) + "_error_" + field._key} className={TW_FormulaError}>{valueStr}</span> :
                            (valueStr && valueStr !== "") && <Badge key={posToString(position) + "_badge_" + field._key} className={readOnly ? TW_LinkBadgeReadOnly : TW_LinkBadge} style={getValueBadgeStyle(value.value)}>{valueStr}</Badge>}
                    </div>
                </PopoverTrigger>
                <PopoverContent className={TW_PopoverFitViewport + " p-2"} side={isMobile ? "bottom" : "right"}>
                    <LinkEditionForm field={field} parentKey={parentKey} item={item} objectKey={objectKey} fieldKey={fieldKey} relationKey={relationKey} value={value} updateValue={updateValue} searchValue={searchValue} setSearchValue={setSearchValue} searchInputRef={searchInputRef} fieldColumn={fieldColumn} position={position} format={format} />
                </PopoverContent>
            </Popover>
        )
    } else {
        const items = value.value as Item[];
        var valuesStr: string[] = []
        if (items && items.length > 0) {
            valuesStr = items.map((value: Item) => {
                return linkedField ? itemToString(value, linkedField, aggregation, format, t, "", fieldByKeys) : item?.value;
            }).filter((value: string) => value !== null && value !== undefined);
        }
        return (
            <Popover open={isEditing} onOpenChange={setIsDialogOpen}>
                <PopoverTrigger className={readOnly ? TW_LinkCellReadOnly : TW_LinkCell} onKeyDown={onEnterKeyPress} onClick={onClick} tabIndex={0}>
                    <div key={posToString(position) + "_div_" + field._key} className="flex flex-row justify-end items-center gap-1">
                        {items && (items.length > 0) && items.map((value: Item, index: number) => {
                            // The linked record: for a link chain, several ones may show the same value (or none)
                            const valueKey = linkedRecordKey(value, field, fieldByKeys) ?? String(index);
                            if (isFormulaError(value.value, linkedField, fieldByKeys)) {
                                return <span key={posToString(position) + "_error_" + valueKey} className={TW_FormulaError}>{valuesStr[index]}</span>
                            }
                            return <Badge key={posToString(position) + "_badge_" + valueKey} className={readOnly ? TW_LinkBadgeReadOnly : TW_LinkBadge} style={getValueBadgeStyle(value.value)}>{valuesStr[index]}</Badge>
                        })}
                    </div>
                </PopoverTrigger>
                <PopoverContent className={TW_PopoverFitViewport + " p-2"} side={isMobile ? "bottom" : "right"}>
                    <LinkEditionForm field={field} parentKey={parentKey} item={item} objectKey={objectKey} fieldKey={fieldKey} relationKey={relationKey} value={value} updateValue={updateValue} searchValue={searchValue} setSearchValue={setSearchValue} searchInputRef={searchInputRef} fieldColumn={fieldColumn} position={position} format={format} />
                </PopoverContent>
            </Popover>
        )
    }
}


